import * as React from "react";
import * as SharedState from "../../../../../shared-state";
import * as LoadCard from "./load-card";
import * as PropertiesSelector from "../../../../../properties-selector";
import * as SettingsOverride from "../../../../components/settings-overrides";
import * as LanguageTexts from "@genesys/shared/lib/language-texts";
import * as CustomDescriptionBox from "../../../../components/custom-description-box";
import { State, Action, mergeTopLevelCaseOverrides } from "./state";
import { Dispatch } from "@typescript-tea/core";
import { definitionIsValid, PropertyDefinitions } from "../../../../tools";
import { PropertyValueSet } from "@genesys/property";
import { ErrorSeverity, LoadDef } from "./types";
import styled from "styled-components";
import { CustomTab } from "../components";
import { S1 } from "@genesys/ui-elements";
import { getWarningTextKey } from "../../utils";

const Container = styled.div``;

const StyledLabel = styled.label`
  margin-left: 5px;
`;

const StyledPropertyValueDesc = styled(S1)`
  font-weight: 500;
`;

export function View({
  isLoadNameDuplicated,
  state,
  isDisabled,
  propertyDefinitions,
  sharedState,
  loadDef,
  headerType,
  currentTopLevelMoistureLoadSettings,
  currentTopLevelCaseOverrides,
  dispatch
}: {
  readonly state: State;
  readonly isLoadNameDuplicated?: boolean;
  readonly headerType: "single" | "multi";
  readonly isDisabled: boolean;
  readonly propertyDefinitions: PropertyDefinitions;
  readonly sharedState: SharedState.State;
  readonly loadDef: LoadDef;
  readonly currentTopLevelCaseOverrides: SettingsOverride.SettingsOverrides;
  readonly currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet;
  readonly dispatch: Dispatch<Action>;
}) {
  const title = sharedState.translate(
    LanguageTexts.globalPropertyName(loadDef.title)
  );

  const isLoadNameEmpty = state.name === "";

  const titleJSx =
    headerType === "single" ? (
      <div style={{ display: "flex", alignContent: "center" }}>
        <CustomDescriptionBox.View title={title}>
          <StyledPropertyValueDesc>
            {sharedState.translate(
              LanguageTexts.globalPropertyName(loadDef.description)
            )}
          </StyledPropertyValueDesc>
        </CustomDescriptionBox.View>
        <StyledLabel>{title.toUpperCase()} </StyledLabel>
      </div>
    ) : (
      <CustomTab
        width="125px"
        value={state.name}
        isReadOnly={isDisabled}
        onChange={value => dispatch(Action.setLoadName(value))}
        hasError={
          (isLoadNameDuplicated ?? false) || isLoadNameEmpty ? "error" : "none"
        }
      />
    );
  return (
    <Container>
      <LoadCard.View
        isLoadNameEmpty={isLoadNameEmpty}
        isLoadNameDuplicated={isLoadNameDuplicated ?? false}
        titleJSX={titleJSx}
        allPropertyDefinitions={propertyDefinitions}
        caseOverrides={SettingsOverride.getSettings(
          SettingsOverride.init(
            mergeTopLevelCaseOverrides(state, currentTopLevelCaseOverrides)
          )
        )}
        isDisabled={isDisabled}
        load={loadDef}
        propertiesSelectorState={PropertiesSelector.init(
          PropertyValueSet.merge(
            state.settings,
            currentTopLevelMoistureLoadSettings
          )
        )}
        sharedState={sharedState}
        operationTimeState={state.operationTimeState}
        removeTypeFromLoad={type => {
          dispatch(Action.removeTypesFromLoad(type, loadDef));
        }}
        onOperationalTimeDispatch={Dispatch.map(
          action => Action.dispatchOperationTime(action),
          dispatch
        )}
        caseOverridesDispatch={Dispatch.map(
          action => Action.dispatchCaseOverrides(action, loadDef),
          dispatch
        )}
        addTypeToLoad={type => {
          dispatch(Action.addTypeToLoad(loadDef, type));
        }}
        propertiesSelectorDispatch={Dispatch.map(
          action =>
            Action.dispatchPropertiesSelector(
              action,
              loadDef.properties.loadProperties,
              loadDef.properties.manualInputKey,
              loadDef.properties.overridableProperties
            ),
          dispatch
        )}
      />
    </Container>
  );
}

export function GetErrorSeverity(
  state: State,
  loadDef: LoadDef,
  currentMoistureLoadSettings: PropertyValueSet.PropertyValueSet,
  definitions: PropertyDefinitions
): ErrorSeverity {
  const settings = state.settings;
  const loadPropertyNames = PropertyValueSet.getPropertyNames(settings);
  const loadPropertyDefinitions = definitions.filter(y =>
    loadPropertyNames.includes(y.name)
  );

  const fullsettings = PropertyValueSet.merge(
    settings,
    currentMoistureLoadSettings
  );

  const hasDefinitionError = loadPropertyDefinitions.every(definition => {
    return definitionIsValid(definition, fullsettings);
  });

  const propertiesSelectorState = PropertiesSelector.init(fullsettings);

  const selectedProperties = PropertiesSelector.getSelectedProperties(
    propertiesSelectorState
  )!;

  const hasWarnings = !!getWarningTextKey(
    loadDef.sourceName,
    selectedProperties
  );

  return state.name === "" || !hasDefinitionError
    ? "error"
    : hasWarnings
    ? "warning"
    : "none";
}
